import { Page, Card, Collapsible, Text, Divider, Layout, BlockStack, Button, Toast } from '@shopify/polaris';
import React, { useState } from 'react';
import { PlusIcon, MinusIcon } from '@shopify/polaris-icons';
import { Icon } from '@shopify/polaris';

function CopyCodeButton({ onCopy }) {
    return (
        <Button onClick={onCopy} size="slim" variant="secondary" tone="white" >
            Copy code
        </Button>
    );
}

function FaqComponent() {
    const [openSections, setOpenSections] = useState({});
    const [activeToast, setActiveToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const supportEmail = "info@huptechweb.com";

    const toggleSection = (id) => {
        setOpenSections(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const toggleToast = () => {
        setActiveToast((activeToast) => !activeToast);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setToastMessage('Code copied to clipboard!');
        setActiveToast(true);
    };

    const faqData = [
        {
            id: '1',
            question: "Is the app free to use? What is the pricing?",
            answer: `<p><strong>No</strong>, the app is not free. It is available at an affordable price of <strong>$2.99/month</strong>. This includes features like automatic currency detection, customizable switcher position, price rounding, and more.</p>`
        },
        {
            id: '2',
            question: "Does the app support Shopify Markets?",
            answer: `The app can work with <strong>Shopify Markets</strong>, but it requires a small manual update to your theme to display converted prices correctly.</p>
        <p>Here's what you need to do:</p>

        <ul>
          <li><strong>If you're using the Dawn theme:</strong><br>
            Go to <code>Snippets</code> in your theme's <strong>Edit Code</strong> section. Find <strong><code>price.liquid</code></strong> file and look for the place where the product price is printed. Wrap the price with a <strong> <code>&lt;span class="money"&gt;&lt;/span&gt;</code></strong> tag if it's not already wrapped, or add <strong><code>class="money"</code></strong> to the existing span tag.</li>
          <li><strong>If you're using a different theme:</strong><br>
            Wherever the product price is shown in your code, wrap it with <strong><code>&lt;span class="money"&gt;&lt;/span&gt;</code></strong>
            <br/>
            <div id="copy-button-container"></div>
          </li>
        </ul>

        <p>This small change helps the app recognize and correctly update currency formatting for different markets.</p>`
        },

        {
            id: '3',
            question: "Does the app support custom price rounding?",
            answer: `<p><strong>Yes</strong>, you can enable or disable the <strong>price rounding</strong> feature from the app settings to show neatly rounded prices.</p>`
        },
        {
            id: '4',
            question: "How can I enable or disable price rounding?",
            answer: `<p>Go to your app dashboard and toggle the <strong>Price Rounding</strong> option ON or OFF as per your preference.</p>`
        },
        {
            id: '5',
            question: "Can I hide decimals in the prices?",
            answer: `<p>You can enable or disable <strong>Remove Decimals</strong> from the app settings to show whole number prices (e.g., $25 instead of $25.49).</p>`
        },
        {
            id: '6',
            question: "Why is the currency switcher not displaying in my store?",
            answer: `<p>Email us at <strong>${supportEmail}</strong>. We'll respond within 24 working hours.</p><p><em><strong>Note:</strong> Disable any other currency switcher to avoid conflicts.</em></p>`
        },
        {
            id: "7",
            question: "How can I change the position of the currency switcher?",
            answer: `<p>Go to <strong>Design</strong> tab in the app and select the <strong>Fixed position</strong> option and select one of the <strong>checkbox</strong> from the corners.</p>`
        },
        {
            id: "8",
            question: "How can I display the currency switcher in the header?",
            answer: `<p>Go to <strong>Design</strong> tab in the app and  look for the <strong>Switcher Location</strong> section and select the <strong>Inline Header</strong> option and follow the steps to display the switcher in the header.</p>`
        },
        {
            id: "9",
            question: "Can I customize the switcher position further?",
            answer: `<p>Our team can customize it based on your store layout. Please provide staff access and send the requirement to <strong>${supportEmail}</strong>.</p>`
        },
        {
            id: "10",
            question: "Why is the switcher not visible on mobile devices?",
            answer: `<p>The switcher appears inside the menu on mobile. For customization, add us as staff and we'll configure it accordingly.</p>`
        },
        {
            id: "11",
            question: "How can I display the price format like $23 USD instead of $23?",
            answer: `<p>Go to <strong>Design</strong> tab in the app and look for the <strong>Design Settings</strong> section and select the <strong>With Currency</strong> option from the <strong>Price Format</strong> dropdown .</p>`
        },
        {
            id: "12",
            question: "What should I do if the currency code is not showing?",
            answer: `<p>If the currency code isn't displayed, please provide us staff access. We'll review and update it within 24 hours.</p>`
        },
        {
            id: "13",
            question: "How do I change the color of the switcher?",
            answer: "<p>Navigate to <strong>Design → Design Settings → Look → Advanced Customization</strong>.</p>"
        },
        {
            id: "14",
            question: "How do I enable or disable the cart page notice?",
            answer: `<p>The <strong>cart notice</strong> is automatically enabled once the app is successfully installed.</p>`
        },
        {
            id: "15",
            question: "Why doesn't the currency change on the checkout page?",
            answer: `<p>The app supports currency conversion only up to the <strong>cart page</strong>. Due to Shopify's platform restrictions, currency changes do <strong>not</strong> reflect on the <strong>checkout page</strong>.</p>`
        },
        {
            id: "16",
            question: "How do I grant staff access to your team?",
            answer: `
        <ul>
          <li><strong>Log in</strong> to your Shopify admin panel.</li>
          <li>Go to <strong>Settings → Users and Permissions</strong>.</li>
          <li>Click on <strong>"Add staff"</strong>.</li>
          <li>Enter staff name as <strong>"Huptech Web Currency Convertor"</strong> and email as <strong>${supportEmail}</strong>.</li>
          <li>Select the following permissions:
            <ul>
              <li><strong>Orders</strong></li>
              <li><strong>Manage and Install Apps and Channels</strong></li>
              <li><strong>Themes</strong></li>
              <li><strong>Manage Settings</strong></li>
            </ul>
          </li>
          <li>Click <strong>"Send invite"</strong> to grant access.</li>
          <li>Once done, please email us at <strong>${supportEmail}</strong> so we can proceed with the setup.</li>
        </ul>`
        },
        {
            id: "17",
            question: "Does the app support language switchers?",
            answer: "<p>The app does <strong>not</strong> integrate with <strong>language switchers</strong>.</p>"
        },
        {
            id: "18",
            question: "Is the app compatible with Online Store 2.0 themes?",
            answer: "<p><strong>Yes</strong>, the app supports <strong>Online Store 2.0</strong> themes.</p>"
        },
        {
            id: "19",
            question: "How can I set the default country for currency conversion?",
            answer: `<p>To set the default country for currency conversion, go to the <strong>General</strong> tab in the app, then scroll down to the <strong>Currency Settings</strong> section. There, you can choose your <strong>Default Currency</strong> from the dropdown list.</p>`
        },
        {
            id: "20",
            question: "FOR DEVELOPERS : How can I trigger the app's currency conversion logic after AJAX, quick view, or other dynamic storefront updates?",
            answer: `<p>If the currency converter is not rendering after your custom frontend interactions such as AJAX calls, quick view modals, or any dynamic DOM updates, you can manually trigger the conversion logic by calling:</p>
             <pre><strong><code>window.hwCurrencyConverterLoad();</code></strong></pre>
             <div id="copy-button-container"></div>
             <p>This function reinitializes the currency formatting across any newly rendered or updated elements on the page. It is recommended to call this function immediately after your custom JavaScript manipulations involving product prices or dynamic content.</p>`
        }

    ];

    const toastMarkup = activeToast ? (
        <Toast content={toastMessage} onDismiss={toggleToast} />
    ) : null;

    return (
        <Page title="Frequently Asked Questions">
            {toastMarkup}
            <Layout>
                <Layout.Section>
                    <Card>
                        <BlockStack vertical spacing="loose">
                            <Text as="p" variant="bodyMd">
                                Find answers to commonly asked questions about our Currency Converter app below. If you can't find what you're looking for, please contact our support team.
                            </Text>
                        </BlockStack>
                        <div style={{ padding: "10px 0" }}>
                            <Divider />
                        </div>

                        <BlockStack vertical spacing="tight">
                            {faqData.map(({ id, question, answer }) => (
                                <div key={id} style={{ marginBottom: "15px", cursor: "pointer" }}>
                                    <Card>
                                        <div
                                            fullWidth
                                            textAlign="left"
                                            onClick={() => toggleSection(id)}
                                            ariaExpanded={openSections[id]}
                                            ariaControls={`${id}-content`}
                                        >
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <Text as="p" variant="bodyMd" fontWeight="bold">{question}</Text>
                                                <Text as="span" variant="bodyMd" color="subdued">
                                                    {openSections[id] ? <Icon source={MinusIcon} /> : <Icon source={PlusIcon} />}
                                                </Text>
                                            </div>
                                        </div>
                                        <Collapsible
                                            id={`${id}-content`}
                                            open={openSections[id]}
                                            transition={{ duration: '150ms', timingFunction: 'ease' }}
                                        >
                                            <div style={{ padding: '16px 0' }}>
                                                {id === '2' || id === '20' ? (
                                                    <>
                                                        <div dangerouslySetInnerHTML={{ __html: answer.split('<div id="copy-button-container"></div>')[0] }} />
                                                        <CopyCodeButton onCopy={() => copyToClipboard(id === '2' ? '<span class=\'money\'></span>' : 'window.hwCurrencyConvertorLoad()')} />
                                                        <div dangerouslySetInnerHTML={{ __html: answer.split('<div id="copy-button-container"></div>')[1] }} />
                                                    </>
                                                ) : (
                                                    <div dangerouslySetInnerHTML={{ __html: answer }} />
                                                )}
                                            </div>
                                        </Collapsible>
                                    </Card>
                                </div>
                            ))}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
}

export default FaqComponent;
